import { useMutation, useQueryClient } from '@tanstack/react-query';

import { toast } from '@hivebrite/design-system/Toast';

import { QUERY_KEYS } from 'hb-react/frontoffice/apps/Forum/constants';
import { useForumContext } from 'hb-react/frontoffice/apps/Forum/contexts/ForumContext';
import { followDiscussion } from 'hb-react/frontoffice/apps/Forum/requests';
import type { Discussion } from 'hb-react/frontoffice/apps/Forum/types';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';

interface FollowProps {
  discussionId: number;
  follow: boolean;
  onSuccess?: (b: boolean) => void;
}

export const useFollowDiscussion = ({
  discussionId,
  follow,
  onSuccess,
}: FollowProps) => {
  const { topicId } = useTopicContext();
  const { configUrls } = useForumContext();
  const queryClient = useQueryClient();

  const { isPending, mutate } = useMutation({
    mutationFn: () => followDiscussion({ configUrls, discussionId, follow }),
    onSuccess: (discussion: Discussion) => {
      onSuccess?.(discussion.is_following);
    },
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEYS.discussion, discussionId, topicId],
      });

      queryClient.setQueryData<Discussion | undefined>(
        [QUERY_KEYS.discussion, discussionId, topicId],
        (old) =>
          old && {
            ...old,
            is_following: !old.is_following,
          },
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.discussion, discussionId, topicId],
      });
    },

    onError: () => {
      toast.error(__('Sorry, an error occurred'));
    },
  });

  return { isPending, mutate };
};
